import {
  Appointment,
  Patient,
  SchedulingEvent,
  TreatmentStep,
} from '@principle-theorem/principle-core';
import {
  AppointmentCollection,
  ISchedulingEvent,
  type IOrganisation,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  collectionGroupQuery,
  orderBy,
  toTimestamp,
  where,
  type Query,
  type WithRef,
} from '@principle-theorem/shared';
import { BigQueryJobName } from '../../job-names';
import { ISchedulingEventWriteTopicData } from '../../topic-data/topic-data';
import {
  BasicBigQuerySyncHandler,
  IBigQuerySyncRequest,
} from '../big-query-sync';

export class BigQuerySchedulingEventSyncHandler extends BasicBigQuerySyncHandler<
  ISchedulingEvent,
  ISchedulingEventWriteTopicData
> {
  readonly bigQueryJobName = BigQueryJobName.SchedulingEventWrite;

  getQuery(request: IBigQuerySyncRequest): Query<ISchedulingEvent> {
    return collectionGroupQuery<ISchedulingEvent>(
      AppointmentCollection.SchedulingEvents,
      where('scheduledByPractice', '==', request.practiceRef),
      where('scheduledAt', '>=', request.range.from),
      where('scheduledAt', '<=', request.range.to),
      orderBy('scheduledAt')
    );
  }

  async getTopicData(
    schedulingEvent: WithRef<ISchedulingEvent>
  ): Promise<ISchedulingEventWriteTopicData> {
    const appointmentRef = SchedulingEvent.appointmentRef(schedulingEvent);
    const appointment = await Firestore.getDoc(appointmentRef);
    const patientRef = Appointment.patientRef(appointment);
    const patient = await Firestore.getDoc(patientRef);
    const brandRef = Patient.brandRef(patient);
    const organisationRef = Firestore.getParentDocRef<IOrganisation>(brandRef);
    const treatmentCategory = await TreatmentStep.resolveTreatmentCategory(
      appointment.treatmentPlan.treatmentStep.display
    );
    const appointmentRequest = appointment.appointmentRequestRef
      ? await Firestore.getDoc(appointment.appointmentRequestRef)
      : undefined;

    return {
      organisation: await Firestore.getDoc(organisationRef),
      brand: await Firestore.getDoc(brandRef),
      practice: await Firestore.getDoc(appointment.practice.ref),
      patient,
      appointment,
      treatmentCategory,
      appointmentRequest,
      schedulingEvent,
      timestamp: toTimestamp(),
    };
  }
}
